/*
  Complete your rig (sections/complete-your-rig.liquid)

  Adds the ticked accessory variants to the cart in the same request as the
  parent product. Loaded with defer, and only on product pages that render at
  least one rig card.

  - Nothing ticked: this file does nothing and the theme's own add-to-cart
    path runs untouched.
  - Something ticked: the submit of the buy-box form is intercepted at
    document capture (ahead of theme.AjaxProduct), the main item is read from
    FormData (so the form-associated quantity input, properties and
    selling_plan are honoured), and ONE JSON POST to theme.routes.cartAdd
    carries the parent plus every ticked variant at quantity 1.
  - Success: one ajaxProduct:added per returned line on the main form
    (GA4 listens there), then the theme's cart-page redirect.
  - Failure: /cart.js is diffed against a pre-POST snapshot. Parent landed =
    dispatch the landed /cart.js lines unchanged (post-merge quantity, the
    same shape the success path dispatches; the GA4 listener derives the
    added quantity from its own snapshot) and go to the cart (the cart is
    the truthful statement of what was added). Parent did not land = stay,
    show the Shopify description in the theme's own div.errors, untick the
    cards.
  - Colour follow: an untouched select follows the parent option1; when no
    option matches and the select carries a placeholder option, it returns
    to the placeholder and the tick locks again (same rule as render time).
    The follow listens to the theme's container-level 'variantChange' (at
    document capture, gated on the target containing the buy-box form), so
    a quick-shop modal's variant picker cannot move the rig.
  - While a rig request is in flight every submit of the buy-box form is
    swallowed, ticked or not, so no second rig POST and no parallel theme
    POST of the parent can start.
  - The in-section button's form= attribute is re-pointed at the live
    buy-box form id on init, so a customiser-added instance (random section
    key) still submits the buy-box form.
  - The buy-box form's named controls shadow HTMLFormElement properties (its
    <select name="id"> makes the form's id property the select, not the id
    string), so the form id is always read with getAttribute('id') and DOM
    calls on the form go through the Element prototype, never the instance.
  - Running summary: the ticked count plus a total summed from the
    server-rendered price strings, shown only when every ticked price shares
    one prefix, suffix and decimal shape (a mixed-market page shows the count
    alone). The grouping and decimals of the total mirror the inputs; nothing
    here formats currency on its own.
  - Quick look: each card's server-rendered <dialog> opens with showModal();
    page scroll is locked while open, a backdrop click or the close button
    closes it (Escape is native) and focus returns to the Quick look button.
    No <dialog> support = the button follows the card's View details link.
  - While a rig request is in flight the rig button is disabled and carries
    the theme's loading class; the cart page is the confirmation.
  - No customer strings live here; the buyer only ever reads what Shopify
    returned. No currency formatting: prices are server-rendered on the
    option elements. ASCII only.
*/
(function () {
  'use strict';

  if (window.__rsRigBound) return;
  window.__rsRigBound = true;

  var WALLET_OFF = 'rs-rig-wallet-off';
  var LOADING = 'btn--loading';
  var CARD_ON = 'rs-rig-card--on';

  var busy = false;
  var parentAvailable = true;

  function routes() {
    return (window.theme && window.theme.routes) || {};
  }

  function cartType() {
    return window.theme && window.theme.settings ? window.theme.settings.cartType : null;
  }

  function rigRoot() {
    return document.querySelector('[data-rs-rig]');
  }

  // HTMLFormElement is [LegacyOverrideBuiltIns]: a control named "id" (the
  // theme's <select name="id">) shadows the form's id property with the
  // select itself, and any named control can shadow a built-in the same way.
  // So the form's attributes are read with getAttribute and DOM calls on the
  // form go through the Element prototype, never the instance. mainForm()
  // itself uses attribute selectors, which named controls cannot touch.
  var EP = (typeof Element !== 'undefined' && Element.prototype) || null;

  function formId(form) {
    return form ? (form.getAttribute('id') || '') : '';
  }

  function formQuery(form, selector) {
    return (EP ? EP.querySelector : form.querySelector).call(form, selector);
  }

  function formAppend(form, node) {
    (EP ? EP.append : form.append).call(form, node);
  }

  function formDispatch(form, evt) {
    return (EP ? EP.dispatchEvent : form.dispatchEvent).call(form, evt);
  }

  // Buy-box form id: AddToCartForm-<main section id>-<product.id>, where the
  // OS 2.0 section id ends in __main. Matched by prefix + suffix, never by
  // the template id.
  function mainForm() {
    var root = rigRoot();
    var id = root ? root.getAttribute('data-rs-rig-product') : '';
    var form = null;
    if (id) {
      form = document.querySelector('form.product-single__form[id^="AddToCartForm-"][id$="__main-' + id + '"]');
    }
    if (!form) {
      form = document.querySelector('[data-product-blocks] form.product-single__form');
    }
    return form;
  }

  // Same selector theme.AjaxProduct uses for the add-to-cart button.
  function atcButton(form) {
    if (!form) return null;
    return formQuery(form, '.add-to-cart') || formQuery(form, 'button[type="submit"]');
  }

  function rigSubmit() {
    return document.querySelector('[data-rs-rig-submit]');
  }

  function paymentWrap() {
    return document.querySelector('[data-rs-payment-wrap]');
  }

  function tickedInputs() {
    return document.querySelectorAll('[data-rs-rig-tick]:checked:not(:disabled)');
  }

  // A server-rendered price string split into prefix, integer part, two
  // decimals and suffix - only the plain shapes Shopify's {{amount}} family
  // prints (optional comma grouping, optional .00). Anything else is null and
  // the summary shows the count alone.
  function parsePrice(text) {
    var m = /^([^\d]*)(\d{1,3}(?:,\d{3})+|\d+)(\.\d{2})?([^\d]*)$/.exec(String(text || '').replace(/^\s+|\s+$/g, ''));
    if (!m) return null;
    return {
      prefix: m[1],
      whole: m[2],
      dec: m[3] ? m[3].slice(1) : null,
      suffix: m[4],
      grouped: m[2].indexOf(',') >= 0
    };
  }

  // Sum of the ticked cards' displayed prices in the same shape, or null when
  // the shapes differ (prefix, suffix or decimals - a mixed-market page) or a
  // price cannot be read. Grouping mirrors the inputs: commas when any input
  // had them or none proved the shop prints 4+ digits bare.
  function sumPrices(ticked) {
    var first = null;
    var cents = 0;
    var grouped = false;
    var bare = false;
    for (var i = 0; i < ticked.length; i++) {
      var card = ticked[i].closest('.rs-rig-card');
      var node = card ? card.querySelector('[data-rs-rig-price]') : null;
      var p = parsePrice(node ? node.textContent : '');
      if (!p) return null;
      if (!first) {
        first = p;
      } else if (p.prefix !== first.prefix || p.suffix !== first.suffix || (p.dec === null) !== (first.dec === null)) {
        return null;
      }
      cents += parseInt(p.whole.replace(/,/g, ''), 10) * 100 + (p.dec === null ? 0 : parseInt(p.dec, 10));
      if (p.grouped) grouped = true;
      if (!p.grouped && p.whole.length > 3) bare = true;
    }
    if (!first) return null;
    var whole = String(Math.floor(cents / 100));
    var frac = cents % 100;
    if (grouped || !bare) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (first.dec !== null) whole = whole + '.' + (frac < 10 ? '0' + frac : String(frac));
    return first.prefix + whole + first.suffix;
  }

  // "<n> selected - <total>": the count and total nodes are server-rendered
  // (label and separator included); the total span hides when there is no
  // safe sum.
  function syncSummary(count, ticked) {
    var countNode = document.querySelector('[data-rs-rig-count]');
    var wrap = document.querySelector('[data-rs-rig-total-wrap]');
    var totalNode = document.querySelector('[data-rs-rig-total]');
    var total = count > 0 ? sumPrices(ticked) : null;
    if (countNode) countNode.textContent = String(count);
    if (totalNode) totalNode.textContent = total === null ? '' : total;
    if (wrap) wrap.hidden = total === null;
  }

  // Submit button, selected-card class, summary and wallet wrapper follow the
  // tick count and the parent variant availability; the button also stays
  // disabled while a request is in flight. The wallet class is rig-owned:
  // theme.js only ever toggles its own "hide" class on the same wrapper.
  function syncState() {
    var ticked = tickedInputs();
    var count = ticked.length;
    var btn = rigSubmit();
    var wrap = paymentWrap();
    if (btn) btn.disabled = busy || !(parentAvailable && count > 0);
    if (wrap) {
      if (count > 0) {
        wrap.classList.add(WALLET_OFF);
      } else {
        wrap.classList.remove(WALLET_OFF);
      }
    }
    var ticks = document.querySelectorAll('[data-rs-rig-tick]');
    for (var i = 0; i < ticks.length; i++) {
      var card = ticks[i].closest('.rs-rig-card');
      if (card) card.classList.toggle(CARD_ON, ticks[i].checked && !ticks[i].disabled);
    }
    syncSummary(count, ticked);
    return count;
  }

  function setLoading(form, on) {
    var atc = atcButton(form);
    var rig = rigSubmit();
    if (atc) atc.classList[on ? 'add' : 'remove'](LOADING);
    if (rig) rig.classList[on ? 'add' : 'remove'](LOADING);
  }

  function release(form) {
    setLoading(form, false);
    busy = false;
    syncState();
  }

  // Mirror the chosen option into the card AND its quick look dialog (both
  // live in the same .rs-rig-item): checkbox value, price text, compare-at
  // text. The placeholder option (empty value) locks the tick.
  function applyOption(select) {
    var item = select.closest('.rs-rig-item') || select.closest('.rs-rig-card');
    if (!item) return;
    var opt = select.options[select.selectedIndex];
    var value = opt ? opt.value : '';
    var tick = item.querySelector('[data-rs-rig-tick]');
    var prices = item.querySelectorAll('[data-rs-rig-price]');
    var wases = item.querySelectorAll('[data-rs-rig-was]');
    var i;
    if (tick) {
      tick.value = value;
      if (value) {
        tick.disabled = false;
      } else {
        tick.checked = false;
        tick.disabled = true;
      }
    }
    var priceText = value && opt ? (opt.getAttribute('data-rs-price') || '') : '';
    for (i = 0; i < prices.length; i++) {
      prices[i].textContent = priceText;
    }
    var compare = value && opt ? (opt.getAttribute('data-rs-compare') || '') : '';
    for (i = 0; i < wases.length; i++) {
      wases[i].textContent = compare;
      wases[i].hidden = !compare;
    }
  }

  function untickAll() {
    var ticks = document.querySelectorAll('[data-rs-rig-tick]');
    for (var i = 0; i < ticks.length; i++) ticks[i].checked = false;
  }

  function removeErrors(form) {
    var errors = formQuery(form, '.errors');
    if (errors) errors.remove();
  }

  function dispatchAdded(form, line) {
    formDispatch(form, new CustomEvent('ajaxProduct:added', {
      bubbles: true,
      detail: { product: line, addToCartBtn: null }
    }));
  }

  function goToCart() {
    var page = routes().cartPage;
    if (!page) return false;
    window.location = page;
    return true;
  }

  function getCart() {
    return fetch(routes().cart, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (res) { return res.json(); });
  }

  // Quantity per variant_id, summed across lines (automatic discounts can
  // split one variant over several lines).
  function totals(cart) {
    var map = {};
    var lines = (cart && cart.items) || [];
    for (var i = 0; i < lines.length; i++) {
      var id = lines[i].variant_id;
      map[id] = (map[id] || 0) + (parseInt(lines[i].quantity, 10) || 0);
    }
    return map;
  }

  // The parent item exactly as theme.js would have posted it, read from the
  // form's entry list (includes form-associated controls outside the form).
  function buildMainItem(form) {
    var fd = new FormData(form);
    var item = {
      id: parseInt(fd.get('id'), 10),
      quantity: parseInt(fd.get('quantity'), 10) || 1
    };
    var properties = null;
    fd.forEach(function (value, key) {
      if (key.indexOf('properties[') === 0 && key.charAt(key.length - 1) === ']') {
        properties = properties || {};
        properties[key.slice(11, -1)] = value;
      } else if (key === 'selling_plan' && value !== '') {
        item.selling_plan = value;
      }
    });
    if (properties) item.properties = properties;
    return item;
  }

  function onSuccess(form, lines) {
    for (var i = 0; i < lines.length; i++) dispatchAdded(form, lines[i]);
    removeErrors(form);
    if (cartType() === 'page' && goToCart()) return; // busy stays true across the unload
    release(form);
  }

  // Same div theme.AjaxProduct.error builds: description string, or message
  // when description is an object. Nothing printed when there is nothing
  // from the server.
  function showError(form, data) {
    removeErrors(form);
    if (!data || !data.description) return;
    var div = document.createElement('div');
    div.classList.add('errors', 'text-center');
    div.textContent = typeof data.description === 'object' ? String(data.message || '') : String(data.description);
    formAppend(form, div);
    formDispatch(form, new CustomEvent('ajaxProduct:error', {
      bubbles: true,
      detail: { errorMessage: data.description }
    }));
  }

  function onFailure(form, mainId, before, data) {
    return getCart().then(function (after) {
      var was = totals(before);
      var now = totals(after);
      if ((now[mainId] || 0) > (was[mainId] || 0)) {
        var lines = (after && after.items) || [];
        var seen = {};
        for (var i = 0; i < lines.length; i++) {
          var id = lines[i].variant_id;
          var delta = (now[id] || 0) - (was[id] || 0);
          if (delta > 0 && !seen[id]) {
            seen[id] = true;
            dispatchAdded(form, lines[i]);
          }
        }
        removeErrors(form);
        if (goToCart()) return;
        release(form);
        return;
      }
      release(form);
      untickAll();
      syncState();
      showError(form, data);
    });
  }

  function onSubmit(evt) {
    var form = mainForm();
    if (!form || evt.target !== form) return;
    if (busy) {
      // A rig request is in flight (or the cart redirect is unloading the
      // page): swallow EVERY submit of the buy-box form, ticked or not, so a
      // buyer who unticks and presses again cannot start a parallel
      // theme.AjaxProduct POST of the parent. busy is only ever true inside
      // that window, so the stock path is never touched.
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }
    var ticked = tickedInputs();
    if (!ticked.length) return; // stock path, untouched
    evt.preventDefault();
    evt.stopPropagation();
    busy = true;

    var main = buildMainItem(form);
    var payload = { items: [main] };
    for (var i = 0; i < ticked.length; i++) {
      payload.items.push({ id: parseInt(ticked[i].value, 10), quantity: 1 });
    }
    setLoading(form, true);
    syncState(); // busy is set: the rig button goes disabled under the spinner

    var snapshot = null;
    getCart().then(function (cart) {
      snapshot = cart;
      return fetch(routes().cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });
    }).then(function (res) {
      return res.json().then(
        function (data) { return { ok: res.ok, data: data }; },
        function () { return { ok: false, data: null }; }
      );
    }).then(function (result) {
      if (result.ok && result.data && result.data.items) {
        onSuccess(form, result.data.items);
        return;
      }
      return onFailure(form, main.id, snapshot, result.data);
    }).catch(function () {
      // Network failure: clear the busy state, leave the ticks, print nothing.
      release(form);
    });
  }

  // Delegated on document so a theme-editor re-render keeps working.
  document.addEventListener('change', function (evt) {
    var target = evt.target;
    if (!target || typeof target.matches !== 'function') return;
    if (target.matches('[data-rs-rig-variant]')) {
      target.setAttribute('data-rs-touched', '');
      applyOption(target);
      syncState();
    } else if (target.matches('[data-rs-rig-tick]')) {
      syncState();
    }
  });

  // theme.Variants dispatches a non-bubbling 'variantChange' on the product
  // section container (the div that holds the buy-box form) and then
  // 'variant:change' on document, with the same detail.variant from the
  // product JSON. Only the container event says WHICH product changed, so the
  // rig listens to that one at document capture (capture still sees a
  // non-bubbling event on a descendant) and acts only when the event target
  // contains the buy-box form. A quick-shop modal (product-recommendations,
  // recently-viewed) runs its own theme.Variants on its own container, so
  // its colour switches never move the rig. Untouched selects follow the
  // parent colour; the submit button follows availability; the wallet class
  // is re-asserted.
  document.addEventListener('variantChange', function (evt) {
    var form = mainForm();
    if (!form || !evt || !evt.target || typeof evt.target.contains !== 'function' || !evt.target.contains(form)) return;
    var variant = evt.detail ? evt.detail.variant : null;
    if (!variant) {
      // No such variant: the theme disables its own button, so does the rig.
      parentAvailable = false;
      syncState();
      return;
    }
    parentAvailable = !!variant.available;
    var selects = document.querySelectorAll('[data-rs-rig-variant]:not([data-rs-touched])');
    for (var i = 0; i < selects.length; i++) {
      var select = selects[i];
      var found = false;
      var placeholder = false;
      for (var j = 0; j < select.options.length; j++) {
        var opt = select.options[j];
        if (opt.value === '') {
          placeholder = true;
        } else if (opt.getAttribute('data-rs-option') === variant.option1) {
          found = true;
          if (select.value !== opt.value) {
            select.value = opt.value;
            applyOption(select);
          }
          break;
        }
      }
      // No match: back to the placeholder (tick locks) when the select has
      // one; a select pre-selected at render (no placeholder) is left alone.
      if (!found && placeholder && select.value !== '') {
        select.value = '';
        applyOption(select);
      }
    }
    syncState();
  }, true);

  document.addEventListener('submit', onSubmit, true);

  // Quick look: the card's server-rendered <dialog> (a sibling of the label
  // inside .rs-rig-item). Delegated on document so a theme-editor re-render
  // keeps working. 'close' does not bubble (Escape fires it too), so it is
  // caught at document capture, like variantChange.
  var peekOpener = null;
  var scrollLock = null;

  function lockScroll(on) {
    var root = document.documentElement;
    var body = document.body;
    if (on) {
      if (scrollLock) return;
      scrollLock = { root: root.style.overflow, body: body ? body.style.overflow : '' };
      root.style.overflow = 'hidden';
      if (body) body.style.overflow = 'hidden';
    } else if (scrollLock) {
      root.style.overflow = scrollLock.root;
      if (body) body.style.overflow = scrollLock.body;
      scrollLock = null;
    }
  }

  function openPeek(btn) {
    var item = btn.closest('.rs-rig-item');
    var dialog = item ? item.querySelector('[data-rs-rig-peek-dialog]') : null;
    if (!dialog || typeof dialog.showModal !== 'function') {
      // No <dialog> support: the card's View details link is the fallback.
      var card = btn.closest('.rs-rig-card');
      var link = card ? card.querySelector('.rs-rig-link') : null;
      var href = link ? link.getAttribute('href') : '';
      if (href) window.location = href;
      return;
    }
    if (dialog.open) return;
    peekOpener = btn;
    lockScroll(true);
    dialog.showModal();
  }

  function closePeek(dialog) {
    if (dialog && dialog.open && typeof dialog.close === 'function') dialog.close();
  }

  document.addEventListener('click', function (evt) {
    var target = evt.target;
    if (!target || typeof target.closest !== 'function') return;
    var opener = target.closest('[data-rs-rig-peek]');
    if (opener) {
      evt.preventDefault();
      openPeek(opener);
      return;
    }
    var closer = target.closest('[data-rs-rig-peek-close]');
    if (closer) {
      closePeek(closer.closest('[data-rs-rig-peek-dialog]'));
      return;
    }
    // A click whose target is the dialog element itself landed on the
    // backdrop: the inner wrapper covers the whole content box and is the
    // only scroller (the section CSS keeps the dialog overflow hidden), so
    // a scrollbar click targets the wrapper, never the dialog.
    if (typeof target.matches === 'function' && target.matches('[data-rs-rig-peek-dialog]')) {
      closePeek(target);
    }
  });

  document.addEventListener('close', function (evt) {
    var target = evt.target;
    if (!target || typeof target.matches !== 'function' || !target.matches('[data-rs-rig-peek-dialog]')) return;
    lockScroll(false);
    var opener = peekOpener;
    peekOpener = null;
    if (opener && typeof opener.focus === 'function') opener.focus();
  }, true);

  // Back/forward cache restore after a redirect: drop the in-flight state so
  // the buttons are usable again.
  window.addEventListener('pageshow', function (evt) {
    if (evt && evt.persisted) {
      release(mainForm());
      syncState();
    }
  });

  function init() {
    var form = mainForm();
    var btn = rigSubmit();
    // The Liquid form= value assumes the section key complete-your-rig; the
    // live form id is the source of truth (customiser-added instances get a
    // random key, so the attribute would point at nothing). Read with
    // getAttribute: the id PROPERTY is the theme's <select name="id">.
    var id = formId(form);
    if (btn && id && btn.getAttribute('form') !== id) {
      btn.setAttribute('form', id);
    }
    var atc = atcButton(form);
    if (atc && atc.disabled) parentAvailable = false;
    syncState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
